// ── LeadsPage — sales leads (staging), with Convert ──
import React, { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { localFilter, exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, ExportBtn, Icon, Modal, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const LeadsPage = () => {
  const { api, setModal, role, go, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [note, setNote] = useState(null);
  // Open-only by DEFAULT. A converted lead is history — it is still reachable by
  // unticking the box, but it shouldn't be the first thing in the way of the
  // list you actually work. view.open (from the dashboard card) already implied
  // this; now it's the resting state.
  const [openOnly, setOpenOnly] = useState(true);

  const load = () => { setLoading(true); api.get("getLeads").then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  // Conversion opens a dialog rather than a yes/no confirm, because a lead is a
  // PERSON: the same company usually has more than one, and converting just the
  // one you clicked strands the rest. See ConvertLeadDialog below.
  const [converting, setConverting] = useState(null);

  const doConvert = (x, alsoLeadIds) => {
    setConverting(null);
    api.post("convertLead", { id: x.id, alsoLeadIds })
      .then(r => {
        load();
        const n = (r && r.extraContacts && r.extraContacts.length) || 0;
        if (n) setNote(`Converted — ${n} additional contact${n === 1 ? "" : "s"} added to the account.`);
        go("sales-opportunities");
      })
      .catch(e => setErr(e.message));
  };

  const base = openOnly ? rows.filter(x => !(x.converted_at || x.converted_accountid)) : rows;
  const filtered = localFilter(base, q, ["companyname", "firstname", "lastname", "ownerName", "lead_source", "status", "email", "phone", "mobilephone"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'createdat', 'desc');

  // Sections, in pipeline order, Converted last — same shape as the portfolio
  // Accounts page. Converted is not a status column value, it's derived from
  // converted_at, so it is resolved first and wins over whatever status the
  // lead was left on.
  const STATUS_ORDER = ["New", "Working", "Qualified", "Unqualified"];
  const CONVERTED = "Converted";
  const statusOf = (x) =>
    (x.converted_at || x.converted_accountid) ? CONVERTED : (x.status || "No Status");

  // Anything with a status not in the list still gets a section rather than
  // disappearing — a typo or a value added in SQL shouldn't hide leads.
  const seen = [...new Set(sorted.map(statusOf))];
  const extras = seen.filter(st => st !== CONVERTED && !STATUS_ORDER.includes(st)).sort();
  const groupOrder = [...STATUS_ORDER, ...extras, CONVERTED];

  const groups = groupOrder
    .map(status => ({ status, rows: sorted.filter(x => statusOf(x) === status) }))
    .filter(g => g.rows.length > 0);

  const sumReq = arr => arr.reduce((s, x) => s + (parseFloat(x.requested_amount) || 0), 0);
  const reset = () => {};   // kept so the search/checkbox handlers stay unchanged

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;
  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Name, company, email, phone..." />,
      <label key="open" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={openOnly} onChange={e => { setOpenOnly(e.target.checked); reset(); }} /> Open only
      </label>,
      role.canEdit ? <Btn key="add" variant="primary" onClick={() => setModal({ type: "lead-add", onDone: load })}>+ Add Lead</Btn> : null,
      <ExportBtn key="ex" onClick={() => exportCSV(sorted, ["companyname", "firstname", "lastname", "ownerName", "status", "lead_source", "requested_amount", "createdat"], "leads.csv")}>Export CSV</ExportBtn>
    ]} />
    {note && (
      <div style={{ fontSize: 13, color: "#0F6E56", background: "#E1F5EE", border: "0.5px solid #B9E3D4",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{note}</div>
    )}

    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <STh label="Company"   sortKey="companyname"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Contact"   sortKey="lastname"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Owner"     sortKey="ownerName"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Status"    sortKey="status"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Source"    sortKey="lead_source"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Commitment"   sortKey="requested_amount" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Created"   sortKey="createdat"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <Th></Th>
        </tr></thead>
        <tbody>
          {groups.map(group => {
            const isConverted = group.status === "Converted";
            return (
              <React.Fragment key={"g-" + group.status}>
                {/* ── Section header ── */}
                <tr>
                  <td colSpan={8} style={{ padding: "8px 12px", background: B.cyanLight }}>
                    <Badge s={group.status} />
                    <span style={{ marginLeft: 8, color: B.cyanDark, fontSize: 11, fontWeight: 600 }}>
                      {group.rows.length} lead{group.rows.length !== 1 ? "s" : ""}
                    </span>
                  </td>
                </tr>

                {/* ── Section rows ── */}
                {group.rows.map(x => (
                  <tr key={x.id} style={{ cursor: "pointer", opacity: isConverted ? 0.7 : 1 }}
                    onClick={() => setModal({ type: "lead-view", rec: x, onDone: load })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td style={{ fontWeight: 500 }}>{x.companyname || "—"}</Td>
                    <Td>{[x.firstname, x.lastname].filter(Boolean).join(" ") || "—"}</Td>
                    <Td>{x.ownerName || "—"}</Td>
                    <Td>{isConverted
                      ? <span style={{ fontSize: 11, color: "#1a7f4b", fontWeight: 600 }}>
                          Converted{x.converted_at ? " · " + fmtDate(x.converted_at) : ""}
                        </span>
                      : (x.status || "—")}</Td>
                    <Td>{x.lead_source || "—"}</Td>
                    <TdNum>{x.requested_amount != null ? fmt(x.requested_amount) : "—"}</TdNum>
                    <Td>{fmtDate(x.createdat)}</Td>
                    <Td><div style={{ display: "flex", gap: 6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                      <Btn onClick={() => setModal({ type: "lead-view", rec: x, onDone: load })}><Icon type="view" /></Btn>
                      {role.canEdit && !isConverted && <Btn onClick={() => setModal({ type: "lead-edit", rec: x, onDone: load })}><Icon type="edit" /></Btn>}
                      {role.canEdit && !isConverted && <Btn variant="primary" onClick={() => setConverting(x)}>Convert</Btn>}
                      {role.canDelete && <Btn onClick={() => setConfirm({ msg: "Delete this lead?", onConfirm: () => delRec("Leads", x.id, "Deleted lead", load) })} variant="danger"><Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}

                {/* ── Section subtotal ── */}
                <tr style={{ background: "var(--color-background-secondary)", borderTop: "1.5px solid " + B.cyanLight }}>
                  <td colSpan={5} style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: B.cyanDark, textAlign: "right" }}>
                    {group.status} Subtotal ({group.rows.length})
                  </td>
                  <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(sumReq(group.rows))}</TdNum>
                  <td colSpan={2} />
                </tr>
              </React.Fragment>
            );
          })}

          {groups.length === 0 && (
            <tr><Td colSpan={8} style={{ color: "var(--color-text-secondary)" }}>
              {openOnly ? "No open leads. Untick \u201cOpen only\u201d to see converted ones." : "No leads."}
            </Td></tr>
          )}
        </tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={5} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({sorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(sumReq(sorted))}</td>
          <td colSpan={2} style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>

    {converting && (
      <ConvertLeadDialog
        lead={converting}
        siblings={siblingsOf(converting, rows)}
        onCancel={() => setConverting(null)}
        onConfirm={(ids) => doConvert(converting, ids)}
        onEdit={() => { const x = converting; setConverting(null); setModal({ type: "lead-edit", rec: x, onDone: load }); }}
      />
    )}
  </>;
};

// Other UNCONVERTED leads that look like the same company: same name, or the
// same email domain. Domain catches "Metpar Corp" vs "Metpar Corporation",
// which is how this goes wrong in practice.
//
// Only leads the user can already see are offered — a restricted BDO never gets
// shown, or gets to fold in, another rep's lead. The API re-checks all of it.
const siblingsOf = (lead, rows) => {
  const norm = v => String(v == null ? "" : v).trim().toLowerCase().replace(/\s+/g, " ");
  const domainOf = v => { const e = norm(v); const i = e.indexOf("@"); return i < 0 ? "" : e.slice(i + 1); };
  const co = norm(lead.companyname);
  const dom = domainOf(lead.email);
  return rows.filter(x =>
    x.id !== lead.id &&
    !(x.converted_at || x.converted_accountid) &&
    ((co && norm(x.companyname) === co) || (dom && domainOf(x.email) === dom))
  );
};

// ── ConvertLeadDialog ────────────────────────────────────────────────────────
// Every sibling starts UNTICKED. Converting somebody's lead is destructive from
// their point of view — it retires the record — so it is opt-in per person, not
// a "we noticed these, they're coming too" default.
// Every lead that becomes a contact needs these. convertLead enforces the same.
const missingForConvert = l => ["firstname", "lastname", "email"]
  .filter(k => !String(l[k] ?? "").trim())
  .map(k => ({ firstname: "First Name", lastname: "Last Name", email: "Email" })[k]);

const ConvertLeadDialog = ({ lead, siblings, onCancel, onConfirm, onEdit }) => {
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const chosen = siblings.filter(s => picked[s.id]);
  const nameOf = x => [x.firstname, x.lastname].filter(Boolean).join(" ") || x.email || "(no name)";
  const leadMissing = missingForConvert(lead);

  return (
    <Modal title={"Convert " + (lead.companyname || "lead")} onClose={onCancel}>
      <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
        Creates an <strong>Account</strong>, an <strong>Opportunity</strong>, and a
        <strong> Contact</strong> for {nameOf(lead)}. This lead is archived and stays linked to all three.
      </div>

      {leadMissing.length > 0 && (
        <div style={{ fontSize: 13, color: "#993C1D", background: "#FBEAE5", border: "0.5px solid #EBC0B3",
                      borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
          This lead can't be converted yet. Add <strong>{leadMissing.join(", ")}</strong> first —
          every converted lead becomes a contact, and a contact needs a name and an email.
        </div>
      )}

      {leadMissing.length === 0 && siblings.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                        borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
            {siblings.length} other open lead{siblings.length === 1 ? " looks" : "s look"} like the same company.
            Tick anyone who belongs on this account and they'll be added as contacts — one account, one
            opportunity, several people. Leave them unticked and they stay in the lead list.
          </div>

          <div style={{ border: "1px solid var(--color-border-secondary)", borderRadius: 8,
                        maxHeight: 240, overflowY: "auto", marginBottom: 14 }}>
            {siblings.map(sx => { const sxMissing = missingForConvert(sx); return (
              <label key={sx.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 12px",
                                          borderBottom: "0.5px solid var(--color-border-tertiary)",
                                          cursor: sxMissing.length ? "not-allowed" : "pointer", fontSize: 13,
                                          opacity: sxMissing.length ? 0.6 : 1 }}>
                <input type="checkbox" checked={!!picked[sx.id]} style={{ marginTop: 2 }} disabled={sxMissing.length > 0}
                  onChange={e => setPicked(pv => ({ ...pv, [sx.id]: e.target.checked }))} />
                <span>
                  <span style={{ fontWeight: 600 }}>{nameOf(sx)}</span>
                  {sx.title ? <span style={{ color: "var(--color-text-secondary)" }}> · {sx.title}</span> : null}
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                    {sx.companyname || "—"}{sx.email ? " · " + sx.email : ""}{sx.status ? " · " + sx.status : ""}
                  </div>
                  {sxMissing.length > 0 && (
                    <div style={{ fontSize: 11, color: "#993C1D" }}>Missing {sxMissing.join(", ")} — complete the lead to include it.</div>
                  )}
                </span>
              </label>
            ); })}
          </div>
        </>
      )}

      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 16 }}>
        {leadMissing.length > 0 ? "" : chosen.length === 0
          ? "Nothing else will be converted."
          : `${chosen.length} additional lead${chosen.length === 1 ? "" : "s"} will be archived and added as contacts: ${chosen.map(nameOf).join(", ")}.`}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        {leadMissing.length > 0 && onEdit && <Btn onClick={onEdit}>Edit Lead</Btn>}
        <Btn variant="primary" disabled={busy || leadMissing.length > 0}
          onClick={() => { setBusy(true); onConfirm(chosen.map(sx => sx.id)); }}>
          {busy ? "Converting…" : "Convert"}
        </Btn>
      </div>
    </Modal>
  );
};
